import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { navigateToPeerMate } from '../utils/peermateHandoff';

/**
 * LegacyPeerMateRedirect
 * 
 * Reusable route bridge for retired embedded PeerMate URLs:
 * /PeerMate, /Peermatehomepage, /peerchat, /Videochat
 * 
 * Preserves old bookmarks, browser history, and external links by
 * automatically initiating the secure single-use ticket SSO handoff
 * to https://peermate.peer2learn.com.
 * 
 * Never handles JWTs directly, never puts credentials in the URL,
 * and delegates unauthenticated handling directly to navigateToPeerMate().
 */
export default function LegacyPeerMateRedirect() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('connecting'); // 'connecting' | 'unauthenticated' | 'failed'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    const executeHandoff = async () => {
      const result = await navigateToPeerMate();
      if (!isMounted) return;

      if (!result.success) {
        if (result.error === 'Unauthenticated') {
          setStatus('unauthenticated');
        } else {
          setStatus('failed');
          setErrorMessage(result.error || 'Connection failed');
        }
      }
    };

    executeHandoff();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleRetry = async () => {
    setStatus('connecting');
    const result = await navigateToPeerMate();
    if (!result.success) {
      if (result.error === 'Unauthenticated') {
        setStatus('unauthenticated');
      } else {
        setStatus('failed');
        setErrorMessage(result.error || 'Connection failed');
      }
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 border border-slate-100 shadow-xl text-center space-y-6">
        {status === 'connecting' && (
          <div className="space-y-4">
            <div className="w-14 h-14 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <h2 className="text-xl font-bold text-slate-900">
              Connecting to PeerMate AI...
            </h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              Redirecting to your secure, standalone AI learning engine. Please wait a moment.
            </p>
          </div>
        )}

        {status === 'unauthenticated' && (
          <div className="space-y-4">
            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto text-2xl font-bold">
              🔒
            </div>
            <h2 className="text-xl font-bold text-slate-900">
              Authentication Required
            </h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              Please log in to your Peer2Learn student account to access PeerMate AI learning blueprints, tutor chat, and CBT practice.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => navigate('/Loginrole')}
                className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition shadow-md shadow-blue-600/20"
              >
                Log In to Continue
              </button>
              <Link
                to="/"
                className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition text-center"
              >
                Return Home
              </Link>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="space-y-4">
            <div className="w-16 h-16 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mx-auto text-2xl font-bold">
              ⚠️
            </div>
            <h2 className="text-xl font-bold text-slate-900">
              Unable to Launch PeerMate AI
            </h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              {errorMessage || 'A network issue occurred while generating your secure handoff session.'}
            </p>
            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleRetry}
                className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition shadow-md shadow-blue-600/20"
              >
                Retry Launch
              </button>
              <Link
                to="/dashboard"
                className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition text-center"
              >
                Go to Dashboard
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
